/**
 * `components.MuiButton` — sizes, tonal/outlined/contained variants, print hiding.
 * Discoverability: `MuiButton`, `Button`, `variant`, `tonal`, `outlined`.
 *
 * @see https://mui.com/material-ui/api/button/
 *
 */
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';

export function muiButton(theme: Theme): Pick<Components<Theme>, 'MuiButton'> {
  return {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        size: 'medium',
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'add' },
          style: {},
        },
        {
          props: { variant: 'text' },
          style: {},
        },
        {
          props: { variant: 'contained' },
          style: {
            color: theme.palette.text.contrastText,
            '&.Mui-disabled': {
              // make disabled contained button faded main color instead of grey
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.contrastText,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            borderRadius: 6,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'medium' && {
            borderRadius: 8,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'large' && {
            borderRadius: 12,
            ...theme.typography.bodyMedium,
          }),

          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
          // Ensure custom 'tonal' variant uses same paddings as 'contained'
          ...(ownerState.variant === 'tonal' && {
            padding:
              ownerState.size === 'small'
                ? `${theme.spacing(0.5)} ${theme.spacing(1.25)}`
                : ownerState.size === 'large'
                  ? `${theme.spacing(1)} ${theme.spacing(2.75)}`
                  : `${theme.spacing(0.75)} ${theme.spacing(2)}`,
          }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              color: theme.palette.text.primary,
              background: theme.palette.background.paper,
              borderColor: theme.palette.border.main,
              '&:hover': {
                color: theme.palette.primary.main,
                background: theme.palette.primary.light,
                borderColor: theme.palette.border.dark,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color && {
              background: theme.palette.background.paper,
              '&:hover': {
                background:
                  theme.palette[ownerState.color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success']
                    ?.light || theme.palette.primary.light,
              },
            }),
          // Tonal - Primary (default)
          ...(ownerState.variant === 'tonal' &&
            (!ownerState.color || ownerState.color === 'primary') && {
              color: theme.palette.primary.main,
              background: theme.palette.primary.light,
              '&:hover': {
                color: theme.palette.primary.dark,
                background: theme.palette.secondary.light,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Secondary
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'secondary' && {
              color: theme.palette.secondary.main,
              background: theme.palette.secondary.light,
              '&:hover': {
                color: theme.palette.secondary.dark,
                background: alpha(theme.palette.secondary.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Error
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'error' && {
              color: theme.palette.error.main,
              background: theme.palette.error.light,
              '&:hover': {
                color: theme.palette.error.dark,
                background: alpha(theme.palette.error.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Warning
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'warning' && {
              color: theme.palette.warning.main,
              background: theme.palette.warning.light,
              '&:hover': {
                color: theme.palette.warning.dark,
                background: alpha(theme.palette.warning.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Info
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'info' && {
              color: theme.palette.info.main,
              background: theme.palette.info.light,
              '&:hover': {
                color: theme.palette.info.dark,
                background: alpha(theme.palette.info.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Success
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'success' && {
              color: theme.palette.success.main,
              background: theme.palette.success.light,
              '&:hover': {
                color: theme.palette.success.dark,
                background: alpha(theme.palette.success.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          textTransform: 'none',
          textWrap: 'nowrap',
          '@media print': {
            display: 'none',
          },
        }),
      },
    },
  };
}
